// src/oura/docs/skillRun.ts
//
// What a doc made by a skill shows about its run (#3983, ourteam-backend#5268).
//
// Create with kind `skill` answers `{document, run}` with `run.status: "running"`
// and the draft lands in the doc when the run ends. The server serves no field
// on a document that says it was made by a skill, so the run is found where the
// server does list it: the Skill library's slot for the doc type, whose `runs`
// are the last 5 for that type, each with the `documentId` it wrote into. A doc
// whose run is no longer among them shows no run, and says nothing about one.
//
// Pure, so the wording, the cost rule and the polling rule are checked without a screen.

import type { SkillLibrary, SkillRun } from "@service/aeo/clientDocsWrite";
import { agoText } from "./format";

/** How often a running run is read again. */
export const RUN_POLL_MS = 5_000;
/** How long a run is waited for, from when it started: a run that never ends is not polled for ever. */
export const RUN_MAX_WAIT_MS = 20 * 60_000;

/** The run that wrote this doc, from its doc type's slot; null when the slot lists none for it. */
export function runForDoc(library: SkillLibrary | null | undefined, docType: string | null | undefined, docId: string): SkillRun | null {
    if (!library || !docType) return null;
    const slot = library.slots.find((s) => s.docType === docType);
    return slot?.runs.find((r) => r.documentId === docId) ?? null;
}

/** What is known of a doc's run between two reads of the library. */
export interface RunKnowledge {
    run: SkillRun | null;
    /** The library has listed this doc's run at least once. */
    observed: boolean;
}

/**
 * The knowledge after one SUCCESSFUL read of the library, `found` being the doc's
 * run in it or null. The run Create answered with is kept until the library first
 * lists it (propagation). Once it HAS been listed, a later read that no longer
 * lists it means it left the library's window (the last 5 runs per doc type,
 * across every client): it is cleared, not kept as the `running` run it last was,
 * which would say the draft is being written for ever (Jack, #3983). A failed
 * read is not this function's business: the caller keeps what it knew.
 */
export function afterLibraryRead(was: RunKnowledge, found: SkillRun | null): RunKnowledge {
    if (found) return { run: found, observed: true };
    if (was.observed) return { run: null, observed: true };
    return was;
}

/**
 * Read the library again? While the run is `running`, within the wait from when
 * it started, or (a doc just created by a skill, whose run the library has not
 * listed yet) while it is expected and not found, within the wait from `since`.
 */
export function shouldPollRun(run: SkillRun | null, expected: boolean, since: number, now: number): boolean {
    if (run) {
        if (run.status !== "running") return false;
        const startedAt = Date.parse(run.startedAt);
        return now - (Number.isNaN(startedAt) ? since : startedAt) < RUN_MAX_WAIT_MS;
    }
    return expected && now - since < RUN_MAX_WAIT_MS;
}

/** How often the board is read again while a doc on it is being written (ourteam-frontend#4391). */
export const BOARD_RUN_POLL_MS = 10_000;

/**
 * Is a doc on the board still being written: its own run (`StaffDocument.skillRun`)
 * `running`, within the wait from when it started? The board reads itself again
 * while one is, and goes back to its usual pace once none is.
 */
export function boardRunGoing(docs: ReadonlyArray<{ skillRun?: Pick<SkillRun, "status" | "startedAt"> | null }>, now: number): boolean {
    return docs.some((d) => {
        const run = d.skillRun;
        if (!run || run.status !== "running") return false;
        const startedAt = Date.parse(run.startedAt);
        return !Number.isNaN(startedAt) && now - startedAt < RUN_MAX_WAIT_MS;
    });
}

export type RunTone = "gray" | "green" | "rose" | "amber";

/** The status in words a person reads. "Written by the skill" is `done`: the draft is in the doc. */
export function runStatus(run: Pick<SkillRun, "status">): { tone: RunTone; label: string } {
    switch (run.status) {
        case "running":
            return { tone: "gray", label: "Oura is writing the draft…" };
        case "done":
            return { tone: "green", label: "Written by the skill" };
        case "stopped_at_cap":
            return { tone: "amber", label: "Stopped at this month's cap" };
        case "failed":
        default:
            return { tone: "rose", label: "The skill run failed" };
    }
}

/**
 * The tag a board row carries for its doc's run: while it runs, and when it
 * ended without a draft. A run that wrote the draft, or no run, adds nothing:
 * the row is then like any other doc.
 */
export function rowRunTag(run: Pick<SkillRun, "status"> | null | undefined): { tone: RunTone; label: string; going: boolean } | null {
    if (!run || run.status === "done") return null;
    return { ...runStatus(run), going: run.status === "running" };
}

/**
 * A run's cost once it is measured. `null` is not measured and reads as nothing,
 * never "$0" or "$0.00": a figure that was not taken must not look like a free run.
 * A measured amount under a cent says so rather than rounding to zero.
 */
export function costText(costUsd: number | null | undefined): string | null {
    if (typeof costUsd !== "number" || Number.isNaN(costUsd)) return null;
    if (costUsd > 0 && costUsd < 0.01) return "under $0.01";
    return `$${costUsd.toFixed(2)}`;
}

/** The sentence a failed or stopped run carries (the server's own), else a plain one; null for a run that did not fail. */
export function runError(run: Pick<SkillRun, "status" | "error">): string | null {
    if (run.status !== "failed" && run.status !== "stopped_at_cap") return null;
    const own = run.error?.trim();
    if (own) return own;
    return run.status === "stopped_at_cap" ? "The run stopped because skill runs reached this month's cap." : "The run did not finish.";
}

/** "Started by Angel · 4 min ago · cost $0.42": who, when, and the cost only once measured. */
export function runMeta(run: Pick<SkillRun, "startedBy" | "startedAt" | "costUsd" | "status">, now: number = Date.now()): string {
    const who = run.startedBy ? `Started by ${run.startedBy}` : "Started";
    const when = agoText(run.startedAt, now);
    const cost = costText(run.costUsd);
    return [who, when, cost ? `cost ${cost}` : null].filter(Boolean).join(" · ");
}
